/**
 * Returns the active data-theme on <html> ('cobalt' | 'plasma') and
 * re-renders the caller whenever it changes — via a MutationObserver on
 * the attribute, so it reacts no matter who flips the theme (the
 * QuickSettings gear, the Settings page, or a cross-tab sync).
 *
 * Heatmap renderers snapshot their cell colors from CSS custom
 * properties inside useMemo. Those memos key on this value so a theme
 * swap re-reads the --gv-* vars and repaints immediately, rather than
 * waiting for the next data frame.
 */

import { useEffect, useState } from 'react';

function currentTheme(): string {
  if (typeof document === 'undefined') return 'cobalt';
  return document.documentElement.dataset.theme || 'cobalt';
}

export function useThemeKey(): string {
  const [theme, setTheme] = useState<string>(currentTheme);

  useEffect(() => {
    const el = document.documentElement;
    const obs = new MutationObserver(() => {
      const t = el.dataset.theme || 'cobalt';
      setTheme(prev => (prev === t ? prev : t));
    });
    obs.observe(el, { attributes: true, attributeFilter: ['data-theme'] });
    // Sync once in case the attribute changed between first render and effect.
    setTheme(currentTheme());
    return () => obs.disconnect();
  }, []);

  return theme;
}
